<!--

============================================================================================================
||    AIAssistants page                                                                                        ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Page for managing AI assistants.

|   Features
------------------------------------------------------------------------------------------------------------
  - No assistants created view
  - List of assistants
  - Filter assistants
  - Create new assistant

|   TODO
------------------------------------------------------------------------------------------------------------
  - loading flows
  - scroll sticky header

-->

<template>
<!-- v-click-outside="abortNewAssistant" -->
  <div
    class="flows-wrapper w-100"
    :class="{'announcement-space': showAnnouncement }" >
    <ScrollArea
      v-if="createAssistant"
      ref="stepperScroll" >
      <div
        class="stepper-wrapper sb-margin-x-40">
        <Spinner
          v-if="loadingSchema"
          style="height: 38px;"
          class="my-4 full-width cover"
          :size="20"
          :thickness="2" />
        <Stepper
          v-if="assistantCreateSteps.length"
          class="stepper"
          :steps="assistantCreateSteps"
          finish-btn-text="Create"
          type="images"
          validate-on-press
          :button-loader="creatingAssistant"
          @abort="abortNewAssistant"
          @finish="createNewAssistant3"
          @scrollToTop="toTop" />
      </div>
      <!-- <div class="flex">
        <pre style="width: 50%">{{ assistantsFormSchema }}</pre>
        <pre style="width: 50%">{{ testPopulatedSchema }}</pre>
      </div> -->
    </ScrollArea>
    <InfoCard
      v-else-if="assistantsLoaded && aiAssistants.length === 0"
      :image="require('@/assets/img/no-assistants.svg')"
      title="Set up your first AI assistant"
      subtitle="Create an AI assistant that will communicate with customers 24/7."
      :background-color1="SBColors.white"
      :background-color2="SBColors.greyUltralight" >
      <template slot="action-slot">
        <b-button
          id="create-flow-btn"
          class="flex center-all"
          :disabled="!enableAICreateAndEdit"
          @click="startNewAssistant" >
          <SBIcon
            v-if="enableAICreateAndEdit"
            class="mr-2"
            :icon="SBIconList.PLUS"
            :color="SBColors.blackPrimary"
            :size="8"/>
          <span>{{ enableAICreateAndEdit ? 'Create' : 'Coming soon'}}</span>
        </b-button>
      </template>
    </InfoCard>
    <ScrollArea
      v-else-if="assistantsLoaded && aiAssistants.length > 0"
      ref="flowsScrollbar" >
      <PageTitle
        image="assets/img/SB-Icons/AI-stars.svg"
        title="Assistants"
        class="sb-margin-x-40" />
      <div class="sticky-top">
        <!-- Mask is blocking the lower scrollable layer to become visible above the sticky top -->
        <div class="mask" />

        <PageFilters
          v-model="filterData"
          class="sb-margin-x-40 flow-filters mb-3"
          show-search
          no-debounce
          :hide-new="!enableAICreateAndEdit"
          :filter-numbers="false"
          search-text="Search assistant..."
          @add-new="startNewAssistant" />
      </div>

      <!-- ASSISTANTS list -->
      <BlockTitle
        v-if="aiAssistantsListData.filter(ai => !ai.hide).length"
        class="sb-margin-x-40"
        :title="`ASSISTANTS (${aiAssistantsListData.filter(ai => !ai.hide).length.toString()})`" />
      <div
        v-if="assistantsLoaded && filterData.search && !aiAssistantsListData.some((f) => !f.hide)"
        class="no-data flex column center-all">
        <span
          v-if="filterData.search"
          class="wrapper flex center-all">
          <span class="emoji">😐</span>
          <p>
            We couldn't find anything for
            <strong v-if="filterData.search">{{ filterData.search }}</strong>
          </p>
        </span>
        <span
          v-if="filterData.length"
          class="clear-filters-btn"
          @click="clearFilter" >Clear filter</span>
      </div>
      <template v-else>
        <DraggableList
          v-model="aiAssistantsListData"
          disable
          pointer
          :enable-toggle="false"
          :updating-items="updatingItems"
          @selected="assistantSelected"
          @remove="deleteAssistant"
          @activate="activateAiAssistant" />
      </template>
    </ScrollArea>

    <Spinner
      v-if="!assistantsLoaded && !activateOrganisation"
      class="mt-5"
      :thickness="2" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import randomString from '@/helpers/randomString';
import Spinner from '@/components/common/Spinner';
import NoPermissionMixin from '@/mixins/AccessPermission';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import DraggableList from '@/components/common/DraggableList';
import ScrollArea from '@/components/common/ScrollArea';
import api from '@/helpers/api';
import ToasterMixin from '@/mixins/Toaster';
import PageTitle from '../common/PageTitle';
import PageFilters from '../common/PageFilters';
import BlockTitle from '../common/BlockTitle';
import InfoCard from '../common/InfoCard2';
import Stepper from '../common/Stepper';
// import CreateAssistantFirstStep from './create_assistant/CreateAssistant.first.step';
// import CreateAssistantSecondStep from './create_assistant/CreateAssistant.second.step';
// import CreateAssistantThirdStep from './create_assistant/CreateAssistant.third.step';
// import CreateAssistantForthStep from './create_assistant/CreateAssistant.forth.step';
import StepperJsonSchemaBlank from './StepperJsonSchemaBlank';
import StatusTypes from '../../helpers/enums/statusTypes.enum';
import dummyEmptySchema from './create_assistant/assistant_emptySchema';
import { createFormSchemaBodyFormat, populateFormBody } from '../forms_schema/formSchema.helpers';

export default {
  name: 'AIAssistants',
  components: {
    DraggableList,
    Spinner,
    SBIcon,
    PageTitle,
    PageFilters,
    ScrollArea,
    BlockTitle,
    InfoCard,
    Stepper,
  },
  mixins: [
    NoPermissionMixin,
    UserPermissionsMixin,
    ToasterMixin,
  ],
  data() {
    return {
      filterData: {},
      assistantsLoaded: false,
      createAssistant: false,
      loadingSchema: true,
      assistantsFormSchema: {},
      // assistantCreateSteps: [
      //   CreateAssistantFirstStep,
      //   CreateAssistantSecondStep,
      //   CreateAssistantThirdStep,
      //   CreateAssistantForthStep,
      // ],
      reorderRunning: false,
      preventAbort: false,
      currentScroll: 0,
      creatingAssistant: false,
      SBColors,
      SBIconList,
      loadDummySchema: false,
      // testPopulatedSchema: null,
      flags: {},
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      permissionDenied: 'user/permissionDenied',
      showAnnouncement: 'system/showAnnouncement',
      organisationFeatures: 'organisation/features',
      aiAssistants: 'organisation/aiAssistants',
      enableAICreateAndEdit: 'organisation/enableAICreateAndEdit',
      actionInProgress: 'automation/actionInProgress',
    }),
    assistantCreateSteps() {
      const steps = [];
      if (this.assistantsFormSchema && this.assistantsFormSchema.scheme && this.assistantsFormSchema.scheme.steps) {
        this.assistantsFormSchema.scheme.steps.forEach((step, index) => {
          steps.push({
            component: StepperJsonSchemaBlank,
            stepImage: step.icon,
            dat: {
              stepName: `step-${index}`,
              title: step.title,
              subtitle: step.subtitle,
              jsonSchema: step.formSchema,
              index,
            },
          });
        });
      }

      return steps;
    },
    aiAssistantsListData() {
      return this.aiAssistants.map((assistant) => {
        const assistantFlags = this.flags[assistant.uid] || assistant.flags;
        return {
          ...assistant,
          label: assistant.name,
          image: assistant.avatar,
          hide: false,
          static: true,
          active: true,
          errorFlag: (assistantFlags && assistantFlags.files && assistantFlags.files.fail) || (assistantFlags && assistantFlags.links && assistantFlags.links.fail),
        };
      });
    },
    activateOrganisation() {
      return this.currentOrganisation && (this.currentOrganisation.should_activate_account || this.currentOrganisation.should_reactivate_account);
    },
    updatingItems() {
      return Object.keys(this.actionInProgress);
    },
  },
  watch: {
    $route: {
      handler() {
        if (this.$route.meta.create) {
          this.startNewAssistant();
        }
      },
    },
    aiAssistants: {
      handler() {
        this.aiAssistants.forEach((assistant) => {
          // FLAG - delete files in progress
          if (assistant.flags.files && assistant.flags.files.delete) {
            this.updateInProgress({ action: 'deleteAiFiles', status: StatusTypes.RUNNING, type: 'all', assistantId: assistant.uid });
          }
          // FLAG - upload files in progress
          if (assistant.flags.files && assistant.flags.files.upload) {
            this.updateInProgress({ action: 'uploadAiFiles', status: StatusTypes.RUNNING, type: 'all', assistantId: assistant.uid });
          }
          // FLAG - domain links delete in progress
          if (assistant.flags.links && assistant.flags.links.delete && assistant.flags.links.delete.domain_links) {
            this.updateInProgress({ action: 'webScrapeDelete', status: StatusTypes.RUNNING, type: 'all', assistantId: assistant.uid });
          }
          // FLAG - domain links upload in progress
          if (assistant.flags.links && assistant.flags.links.scrape && assistant.flags.links.scrape.domain_links) {
            this.updateInProgress({ action: 'webScrape', status: StatusTypes.RUNNING, type: 'all', assistantId: assistant.uid });
          }
          // FLAG - specific links delete in progress
          if (assistant.flags.links && assistant.flags.links.delete && assistant.flags.links.delete.specific_links) {
            this.updateInProgress({ action: 'webScrapeDelete', status: StatusTypes.RUNNING, type: 'specific', assistantId: assistant.uid });
          }
          // FLAG - specific links scrape in progress
          if (assistant.flags.links && assistant.flags.links.scrape && assistant.flags.links.scrape.specific_links) {
            this.updateInProgress({ action: 'webScrape', status: StatusTypes.RUNNING, type: 'specific', assistantId: assistant.uid });
          }
        });
      },
      immediate: true,
    },
    activateOrganisation: {
      handler() {
        if (this.activateOrganisation) {
          // Redirect to contacts so the content below blured activation background isn't empty
          this.$router.push({ name: 'OrgContacts', params: { organisationId: this.currentOrganisation.id } }).catch(() => { });
        }
      },
      immediate: true,
    },
    filterData() {
      this.filterAssistantsList();
    },
    permissionDenied() {
      this.assistantsLoaded = true;
    },
    organisationFeatures() {
      if (!this.organisationFeatures || !this.organisationFeatures.organisation_features) {
        return;
      }
      let orgFlows;
      try {
        orgFlows = this.organisationFeatures.organisation_features.FLOWS.find(f => f.code === 'FLOWS');
      } catch (e) {
        orgFlows = {};
      }
      if (!orgFlows || !orgFlows.feature_active) {
        this.$router.push({ name: 'OrgContacts', params: { organisationId: this.currentOrganisation.id } }).catch(() => { });
      }
    },
  },
  mounted() {
    if (this.currentOrganisation.id) {
      this.fetchAiAssistants()
        .then(() => {
          this.assistantsLoaded = true;
        });
    }
    if (this.$route.meta.create) {
      this.startNewAssistant();
    }
    window.eventBus.$on('create-assistant', () => {
      this.startNewAssistant();
    });
    window.eventBus.$on('stepperInitialized', () => {
      this.loadingSchema = false;
    });
    window.eventBus.$on('mark-scroll', () => {
      // get scroll value for the reset
      if (this.$refs && this.$refs.stepperScroll) {
        this.currentScroll = this.$refs.stepperScroll.scrollTop;
      }
    });
    window.eventBus.$on('reset-scroll', () => {
      // reset scroll value ater the changes are made from the component
      if (this.$refs && this.$refs.stepperScroll) {
        this.$refs.stepperScroll.scrollTo(this.currentScroll);
      }
    });
    window.eventBus.$on('ai-files-update', this.aiFilesUpdate);
  },
  destroyed() {
    localStorage.removeItem('formSchemaAvatar');
    window.eventBus.$off('ai-files-update', this.aiFilesUpdate);
  },
  methods: {
    ...mapActions({
      fetchAiAssistants: 'organisation/fetchAiAssistants',
      addBackgroundActions: 'automation/addBackgroundActions',
      updateInProgress: 'automation/updateInProgress',
    }),
    aiFilesUpdate(data) {
      if (!this.flags[data.ai_assistant_uid]) {
        this.flags[data.ai_assistant_uid] = {};
      }
      this.flags[data.ai_assistant_uid] = { ...this.flags[data.ai_assistant_uid], ...data.flags };
      this.flags = { ...this.flags };
    },
    filterAssistantsList() {
      this.aiAssistantsListData.forEach((aiAssistant) => {
        let hide = true;
        if (!this.filterData.search || aiAssistant.label.toLowerCase().indexOf(this.filterData.search.toLowerCase()) >= 0) {
          hide = false;
        }
        aiAssistant.hide = hide;
      });
    },
    abortNewAssistant() {
      if (!this.preventAbort) {
        this.createAssistant = false;
      }
    },
    assistantSelected(assistant, data = null) {
      this.$router.push({
        name: 'AssistantTest',
        params: {
          assistantId: assistant.uid,
          data: data ? JSON.stringify(data) : null,
        },
      });
    },
    createNewAssistant(data) {
      // const frequencyId = data.flowTriggerFrequency.flowTriggerFrequenciesDetails.selected[0].id;
      // const frequencyValue = data.flowTriggerFrequency.flowTriggerFrequenciesDetails.selected[0].additionalData ? data.flowTriggerFrequency.flowTriggerFrequenciesDetails.selected[0].additionalData.seconds : null;
      // // frequencyObject
      // const frequencyObject = {
      //   params: {
      //     trigger_frequency: {
      //       type: frequencyId,
      //     },
      //   },
      // };
      // if (frequencyValue) {
      //   frequencyObject.params.trigger_frequency.value = frequencyValue;
      // }

      // // Trigger group
      // const flowTriggerGroupId = data.flowTrigger.selected[0].id;

      // // Trigger
      // const flowTriggerId = data.flowTriggerDetails.flowTriggerDetails.selected[0].id;
      // let flowTriggerTags;
      // try {
      //   flowTriggerTags = [...data.flowTriggerDetails.flowTriggerDetails.selected[0].additionalData.tags.map(tag => tag.tag)];
      // } catch (error) { /* Do nothing */ }
      // let flowTriggerOperator;
      // try {
      //   flowTriggerOperator = data.flowTriggerDetails.flowTriggerDetails.selected[0].additionalData.condition;
      // } catch (error) { /* Do nothing */ }

      // // Trigger Object
      // const triggerObject = {
      //   blocks: [
      //     {
      //       type: flowTriggerGroupId,
      //       category: 'triggers',
      //       order: 0,
      //       params: {
      //         triggers: [
      //           {
      //             type: flowTriggerId,
      //           },
      //         ],
      //       },
      //     },
      //   ],
      // };

      // if (flowTriggerTags) {
      //   triggerObject.blocks[0].params.triggers[0].value = flowTriggerId === 'full_match' ? flowTriggerTags[0] : flowTriggerTags;
      // }
      // if (flowTriggerOperator) {
      //   triggerObject.blocks[0].params.triggers[0].operator = {
      //     value: flowTriggerOperator,
      //   };
      // }

      // const payload = {
      //   order: this.lastOrder + 1,
      //   // trigger name
      //   name: data.flowName.flowName,
      //   // trigger
      //   ...triggerObject,
      //   ...frequencyObject,
      // };

      // this.createFlow({ numberId: this.createFlowForNumber, data: payload })
      //   .then((response) => {
      //     if (response.status === 200) {
      //       this.createFlowForNumber = null;
      //       this.lastOrder++;
      //       // redirect to the created flow
      //       this.$router.push({
      //         name: 'FlowTree',
      //         params: {
      //           flowId: response.data.flow_data.flow_id,
      //           numberId: response.data.flow_data.business_uid,
      //           flowName: response.data.flow_data.name,
      //           flowActive: response.data.flow_data.active,
      //         },
      //       });
      //     } else {
      //       this.showCreateAssistantError();
      //     }
      //   })
      //   .catch((error) => {
      //     this.showCreateAssistantError(error);
      //   });

      const result = {
        steps: [],
      };
      this.assistantsFormSchema.scheme.steps.forEach((step, index) => {
        const stepData = {
          formSchema: {
            properties: {},
          },
        };
        Object.keys(step.formSchema.properties).forEach((property) => {
          const returnValue = step.formSchema.properties[property].returnProperties;
          if (returnValue) {
            const value = {};
            returnValue.forEach((key) => {
              try {
                if (key === 'value') {
                  const targetValue = data[`step-${index + 1}`][property].data.value;
                  value[key] = targetValue || null;
                } else {
                  value[key] = step.formSchema.properties[property][key];
                }
              } catch (e) { /* */ }
            });
            stepData.formSchema.properties[property] = value;
          }
        });
        result.steps.push(stepData);
      });
      const body = { data: result };

      console.log('BODY', body);

      // return;

      const url = `/organisation/${this.currentOrganisation.id}/assistant/create`;
      api('POST', url, body, null, 'w')
        .then((response) => { });
    },
    createNewAssistant2(data) {
      console.log('DATA', data);
      const result = {
        steps: [],
      };
      // process each step
      this.assistantsFormSchema.scheme.steps.forEach((schemaStep, index) => {
        const stepData = {
          formSchema: {
            properties: {},
          },
        };
        console.log('STEP', index + 1);
        const dataStep = data[`step-${index + 1}`];
        // process each step property
        Object.keys(schemaStep.formSchema.properties).forEach((property) => {
          const propertyValue = this.processProperty(schemaStep.formSchema, dataStep, property);
          if (propertyValue) {
            stepData.formSchema.properties[property] = propertyValue;
          }
        });
        result.steps.push(stepData);
      });
      const body = { data: result };

      console.log('BODY', body);

      const url = `/organisation/${this.currentOrganisation.id}/assistant/create`;
      api('POST', url, body, null, 'w')
        .then((response) => { });
    },
    checkPostActions(data) {
      // check if there are any post actions
      const postActions = [];
      const checkForPostActions = (dataSet) => {
        if (dataSet && dataSet.postAction && dataSet.dirty && dataSet.valid) {
          switch (dataSet.postAction) {
            case 'uploadAiFiles':
              if (!dataSet.data || !dataSet.data.files || !((dataSet.data.files.addFiles && dataSet.data.files.addFiles.length) || (dataSet.data.files.removeFiles && dataSet.data.files.removeFiles.length))) {
                return;
              }
              if (dataSet.data.files.addFiles && dataSet.data.files.addFiles.length) {
                postActions.push({
                  action: 'uploadAiFiles',
                  data: { files: dataSet.data.files.addFiles },
                  assistantId: this.assistantId,
                });
              }
              if (dataSet.data.files.removeFiles && dataSet.data.files.removeFiles.length) {
                postActions.push({
                  action: 'deleteAiFiles',
                  data: { files: dataSet.data.files.removeFiles },
                  assistantId: this.assistantId,
                });
              }
              return;
            default:
              break;
          }
          postActions.push({
            action: dataSet.postAction,
            data: dataSet.data,
            assistantId: this.assistantId,
          });
        } else if (this.isObject(dataSet)) {
          Object.keys(dataSet).forEach((key) => {
            checkForPostActions(dataSet[key]);
          });
        }
      };
      checkForPostActions(data);

      return postActions;
    },
    /**
     * Builds a body object and submits the data to the API.
     * @param {Object} data Form schema values grouped by steps.
     */
    createNewAssistant3(data) {
      // console.log('DATA', data);
      this.creatingAssistant = true;
      const assistantsFormSchemaBodyFormat = createFormSchemaBodyFormat(this.assistantsFormSchema.scheme);
      // console.log('FORMAT');
      // console.log(assistantsFormSchemaBodyFormat);
      // return;
      const assistantsFormSchemaBodyPopulated = populateFormBody(assistantsFormSchemaBodyFormat, data);
      const body = { data: assistantsFormSchemaBodyPopulated };
      // TESTING schema repopulation -----------------------------
      // console.log('BODY');
      // console.log(body);
      // this.testPopulatedSchema = populateEmptySchema(this.assistantsFormSchema, body);
      // this.assistantSelected({ uid: 'aee6a0a7-1a22-4ba0-b7cf-1e470503bb5' }, body);
      // return;
      // TESTING schema repopulation --- END --------------------------
      const postActions = this.checkPostActions(data);
      const url = `/organisation/${this.currentOrganisation.id}/assistant/create`;
      // console.log(body);
      // this.creatingAssistant = false;
      api('POST', url, body, null, 'w')
        .then((response) => {
          if (postActions.length) {
            postActions.forEach((action) => {
              action.assistantId = response.data.assistant_id;
              action.id = randomString(10);
              action.status = StatusTypes.PENDING;
            });
            this.addBackgroundActions(postActions);
            this.$nextTick(() => {
              // update all elements that implement postActions to reset
              this.$eventBus.$emit('resetPostActions');
            });
          }
          this.fetchAiAssistants();
          this.assistantSelected({ uid: response.data.assistant_id });
        })
        .catch((error) => {
          this.showCreateAssistantError(error);
          this.creatingAssistant = false;
        });
    },
    /**
     * DEPRECATED - use the one from formSchema.helpers
     * Populates the body object with the data from the form schema.
     * @param {Object} bodyFormat Form schema body format.
     * @param {Object} data Form schema values grouped by steps.
     */
    populateFormBody_bkp(bodyFormat, data) {
      // Create a copy of a bodySchema to avoid modifying the original schema.
      const copyAssistantsFormSchemaBodyFormat = JSON.parse(JSON.stringify(bodyFormat));

      // Function can be called recursivelly to populate the body object with the data from the form schema.
      // It can also remove the keys that are not present in the data object.
      const populateBody = (schema, path = '') => {
        if (schema && typeof schema === 'object') {
          Object.keys(schema).forEach((key) => {
            // console.log('KEY', key);
            const isObject = typeof schema[key] === 'object';
            const isArray = Array.isArray(schema[key]);
            const returnProperties = JSON.parse(JSON.stringify(schema.returnProperties || []));
            if (key === 'value') {
              // Path needs to be modified to match the data object path.
              const trimmedPath = path.replaceAll(/\.formSchema|\.properties/gi, '');
              // console.log('TRIMMED', trimmedPath);
              // Values can have two different postitions in the data object so we need to check both.
              const dataPath1 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}.data.value` : `${trimmedPath}.data.value`;
              // console.log('DATA PATH 1', dataPath1);
              let pathValue = this.getObjectCopyByPath(data, dataPath1);
              // If the value is not found on the first path, try the second one.
              if (!pathValue) {
                let dataPath2 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}` : trimmedPath;
                const lastPosSlash = dataPath2.lastIndexOf('.');
                dataPath2 = lastPosSlash ? `${dataPath2.substring(0, lastPosSlash)}.data${dataPath2.substring(lastPosSlash)}.data.value` : `.data.${dataPath2}.data.value`;
                // console.log('DATA PATH 2', dataPath2);
                pathValue = this.getObjectCopyByPath(data, dataPath2);
                // If the value is not found on the second path, try the third one. This one checks for the modal schema values.
                if (!pathValue) {
                  let dataPath3 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}` : `${trimmedPath}`;
                  const key = dataPath3.substring(dataPath3.lastIndexOf('.'));
                  dataPath3 = dataPath3.substring(0, dataPath3.lastIndexOf('.modal.schema'));
                  dataPath3 += `.data${key}.data.value`;
                  // console.log('DATA PATH 3', dataPath3);
                  pathValue = this.getObjectCopyByPath(data, dataPath3);
                  // If the value is not found on the third path, try the forth one. This one checks for the modal schema values for object.
                  if (!pathValue) {
                    let dataPath4 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}` : `${trimmedPath}`;
                    dataPath4 = dataPath4.replaceAll(/\.modal\.schema/gi, '.data');
                    // dataPath4 = dataPath4.substring(0, dataPath4.lastIndexOf('.modal.schema'));
                    dataPath4 += '.data.value';
                    // console.log('DATA PATH 4', dataPath4);
                    pathValue = this.getObjectCopyByPath(data, dataPath4);
                  }
                }
              }
              if (pathValue) {
                // console.log('VALUE', pathValue);
                if (Array.isArray(pathValue)) {
                  schema[key] = pathValue.filter(v => v);
                } else {
                  schema[key] = pathValue;
                }
              }
            } else if (isArray && key !== 'returnProperties') {
              // In case the key value is of type Array just iterate through the array and call the function recursively.
              schema[key].forEach((item, index) => populateBody(item, path ? `${path}.${key}.${index}` : `${key}.${index}`));
            } else if (isObject && key !== 'returnProperties') {
              // If we are dealing with 'properties' object then the data path should be checked if it exists in the data object. If it doesn't, remove the key.
              // TODO oportunity for optimisation - check property at the end of a path so no previous properties are being checked
              if (path.indexOf('properties') >= 0) {
                // Path needs to be modified to match the data object path.
                let dataMainPath1 = path.indexOf('steps') >= 0 ? `step-${path.substring(path.indexOf('steps') + 6)}.${key}` : `${path}.${key}`;
                // Values can have two different postitions in the data object so we need to check both.
                dataMainPath1 = dataMainPath1.replaceAll(/\.formSchema|\.properties/gi, '');
                // console.log('DATA MAIN PATH 1', dataMainPath1);
                // const testModalRegex = new RegExp('modal(.schema(.properties){0,1}){0,1}$', 'i');
                // const testModalRegex = new RegExp('modal(.schema(.properties(.*[^.]*)){0,1}){0,1}$', 'i');
                const testModalRegex = new RegExp('modal(\.schema(\.*[^.]*)){0,1}$', 'i');
                if (!testModalRegex.test(dataMainPath1) && dataMainPath1) {
                  let testPath = this.getObjectCopyByPath(data, dataMainPath1);
                  // If the value is not found on the first path, try the second one.
                  if (!testPath) {
                    let dataMainPath2 = path.indexOf('steps') >= 0 ? `step-${path.substring(path.indexOf('steps') + 6)}.data.${key}` : `${path}.data.${key}`;
                    dataMainPath2 = dataMainPath2.replaceAll(/\.formSchema|\.properties/gi, '');
                    // console.log('DATA MAIN PATH 2', dataMainPath2);
                    testPath = this.getObjectCopyByPath(data, dataMainPath2);

                    // If the value is not found on the second path, remove the key from the body.
                    if (!testPath && !returnProperties.includes(key)) {
                      delete schema[key];
                    }
                  }
                }
              }

              // If the key is still present after the check, call the function recursively.
              if (schema[key]) {
                populateBody(schema[key], path ? `${path}.${key}` : key);
              }
            }
          });
        }
      };
      populateBody(copyAssistantsFormSchemaBodyFormat);

      // Run it one more time but remove all 'returnProperties' keys. Those were not possible to remove during the previous run because it was used to remove all the unnecessary parameters firs like the paremeters for the views not selected by user.
      const removeReturnProperties = (schema) => {
        if (schema && typeof schema === 'object') {
          Object.keys(schema).forEach((key) => {
            if (key === 'returnProperties') {
              delete schema[key];
              return;
            }

            const isObject = typeof schema[key] === 'object';
            const isArray = Array.isArray(schema[key]);
            if (isArray) {
              // In case the key value is of type Array filter out all the non object values since those can't be iterated any further, then recursively iterate remaining items,
              // schema[key] = schema[key].filter(item => typeof item === 'object');
              schema[key].forEach(item => removeReturnProperties(item));
            } else if (isObject) {
              // Objects need to be processed recursively.
              removeReturnProperties(schema[key]);
            }
          });
        }
      };

      removeReturnProperties(copyAssistantsFormSchemaBodyFormat);

      return copyAssistantsFormSchemaBodyFormat;
    },
    /**
     * DEPRECATED - use the one from formSchema.helpers
     * Creates form schema body format for the submit based on the input JSON which usually is the schema received from the backend initially.
     * @param {Object} inputJSON Form schema JSON.
     */
    createFormSchemaBodyFormat_bkp(inputJSON) {
      const copyJSON = JSON.parse(JSON.stringify(inputJSON));

      // Processes schema(JSON) by removing everything other than 'returnProperties' and every other key defined by 'returnProperties' at the same level.
      // It does it from the deepest point of JSON input file so if some key meets the conditions to be removed but has children, it will survive because
      // children has already been processed and survived the cleanup because it has 'returnProperties' so the parent shold stay in the final schema too.
      // e.g. There is nothing to be removed here
      // {
      //   a: {
      //     b: {
      //       c: {
      //         returnProperties: ['value'],
      //         value: 'test',
      //       }
      //     }
      //   }
      // }
      const processSchema = (schema) => {
        if (schema && typeof schema === 'object') {
          Object.keys(schema).forEach((key) => {
            const isObject = typeof schema[key] === 'object';
            const isArray = Array.isArray(schema[key]);
            const returnProperties = JSON.parse(JSON.stringify(schema.returnProperties || []));
            if (isArray && key !== 'returnProperties' && !returnProperties.includes(key)) {
              // In case the key value is of type Array filter out all the non object values since those can't be iterated any further, then recursively iterate remaining items,
              // filter out empty objects and finally remove the key if it results with an empty array after the cleanup.
              schema[key] = schema[key].filter(item => typeof item === 'object');
              schema[key].forEach(item => processSchema(item));
              schema[key] = schema[key].filter(item => typeof item === 'object' && item !== null && Object.keys(item).length !== 0 && item.constructor === Object);
              if (!schema[key].length) {
                delete schema[key];
              }
            } else if (!returnProperties.includes(key) && schema[key] === null) {
              // remove all the null values
              delete schema[key];
            } else if (isObject && key !== 'returnProperties' && !returnProperties.includes(key)) {
              // Objects need to be processed recursively but check if it results with an empty object and delete it if it does.
              processSchema(schema[key]);
              if (Object.keys(schema[key]).length === 0 && schema[key].constructor === Object && !returnProperties.includes(key)) {
                delete schema[key];
              }
            } else if (key !== 'returnProperties' && !returnProperties.includes(key)) {
              // Remove all primitive values
              delete schema[key];
            }
          });
        }
      };

      processSchema(copyJSON);

      return copyJSON;
    },
    showCreateAssistantError(error = null) {
      // const message = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Something went wrong. Please try again.';
      this.showToaster('Creation of AI assistant failed.', null, null, 'error');
    },
    deleteAssistant(assistant) {
      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: 'Delete Ai Assistant',
        message: `Are you sure you want to delete <b>${assistant.label}</b> flow??`,
        confirmButtonText: 'Delete',
        showUndoWarning: false,
        autoClose: 'NO',
        confirmCallback: () => {
          // this.deleteFlowVuex({ numberId: assistant.numberId, flowId: assistant.id })
          //   .then((response) => {
          //     if (response.status === 200) {
          //       this.showToaster('Flow deleted successfully.', null, null, 'OK');
          //     } else {
          //       this.showToaster('Something went wrong. Please try again.', null, null, 'error');
          //     }
          //   })
          //   .catch((error) => {
          //     this.showToaster('Something went wrong. Please try again.', null, null, 'error');
          //   })
          //   .finally(() => {
          //     this.$confirmPluginModal.close();
          //   });
        },
      });
    },
    startNewAssistant() {
      this.assistantsFormSchema = {};
      this.preventAbort = true;
      this.createAssistant = true;
      setTimeout(() => {
        this.preventAbort = false;
      }, 500);

      this.loadingSchema = true;

      if (this.loadDummySchema && dummyEmptySchema) {
        // For testing with local emptyScheme
        this.assistantsFormSchema = dummyEmptySchema;
      } else {
        // https://app.dev.number.tm/w/organisation/a63e6863-1e61-457e-b286-a0a22ee67f7a/assistant/empty-scheme
        this.$nextTick(() => {
          api('GET', `organisation/${this.currentOrganisation.id}/assistant/empty-scheme`, null, null, 'w')
            .then((response) => {
              this.assistantsFormSchema = response.data;
            });
        });
      }

      // const mockData = {
      //   scheme: {
      //     "steps": [
      //       {
      //         "title": "General info and preferences",
      //         "subtitle": "Provide general information and preferences for your AI assistant.",
      //         "icon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //         "formSchema": {
      //           "type": "object",
      //           "properties": {
      //             "name": {
      //               "value": "",
      //               "type": "input",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "name",
      //               "valueType": "string",
      //               "title": "Name",
      //               "pickIcon": true,
      //               "placeholder": "Support assistant",
      //               "validators": "maxLength:100",
      //               "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitle": "Only visible to team members in your AI Number account.",
      //               "iconList": [
      //                 "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                 "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                 "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                 "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg"
      //               ],
      //             },
      //               "businessName": {
      //               "value": "",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "business_name",
      //               "valueType": "string",
      //               "type": "input",
      //               "title": "Business name",
      //               "placeholder": "Super Company Inc.",
      //               "validators": "required|maxLength:100",
      //               "warningValidators": "minLength",
      //               "validatorMessages": {
      //                 "minLength": "Add descriptive info to improve the AI's responses.",
      //                 "maxLength": null
      //               },
      //               "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitle": "Add the business the AI works for."
      //             },
      //             "responsibility": {
      //               "value": "",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "...",
      //               "valueType": "string",
      //               "type": "input",
      //               "title": "Responsibility",
      //               "placeholder": "Provide customer support",
      //               "validators": "required|minLength:20|maxLength:100",
      //               "warningValidators": "minLength",
      //               "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitle": "Add the responsibility the AI will perform."
      //             },
      //             "language": {
      //               "value": "fr",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "...",
      //               "valueType": "string",
      //               "type": "dropdown",
      //               "title": "Language",
      //               "placeholder": "Select language",
      //               "validators": "required",
      //               "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitle": "AI will reply in {{ option }}.",
      //               "subtitlePlaceholderDefault": "the contact's language",
      //               "options": [
      //                 { "value": "en", "label": "English"},
      //                 { "value": "es", "label": "Spanish"},
      //                 { "value": "fr", "label": "French"}
      //               ],
      //               "width": 50
      //             },
      //             "currencyFormat": {
      //               "value": "fr",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "...",
      //               "valueType": "string",
      //               "type": "dropdown",
      //               "title": "Currency format",
      //               "placeholder": "USD ($100,00.00)",
      //               "options": [
      //                 { "value": "USD", "label": "USD ($100,00.00)" },
      //                 { "value": "CAD", "label": "CAD (100,00.00$)" },
      //                 { "value": "HR", "label": "HR (100,00.00€)" }
      //               ],
      //               "width": 50,
      //               "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitle": "Select the format that AI will use in replies."
      //             },
      //             "conversation": {
      //               "value": "",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "conversation",
      //               "valueType": "object",
      //               "type": "conversation",
      //               "wrap": true,
      //               "title": "Intro message",
      //               "edit": true,
      //               "subtitleTopPrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitleTop": "AI will start a conversation with this message.",
      //               "placeholder": "Hello, thanks for reaching out. How can we help you today?",
      //               "validators": "maxLength:1024",
      //               "buttons": {
      //                 "value": [],
      //                 "returnProperties": ["value", "group", "variableName", "valueType"],
      //                 "group": "foobar",
      //                 "variableName": "...",
      //                 "valueType": "string",
      //                 "type": "message-buttons",
      //                 "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                 "addButtonText": "Add button",
      //                 "minButtons": 0,
      //                 "maxButtons": 10,
      //                 "enter": "Add",
      //                 "blackButtons": true,
      //                 "validators": "required|maxLength:20",
      //                 "validatorMessages": {
      //                   "required": "Button text is required.",          
      //                   "maxLength": "Please limit your text to {{validator}} characters."      
      //                 },
      //               },
      //             },
      //             // "conversation1": {
      //             //   "value": "abcdefthgđdfh",
      //             //   "returnProperties": ["value", "group", "variableName", "valueType"],
      //             //   "group": "foobar",
      //             //   "variableName": "conversation",
      //             //   "valueType": "object",
      //             //   "type": "conversation",
      //             //   "wrap": true,
      //             //   "title": "Only message - NO buttons or actions",
      //             //   "edit": true,
      //             //   "subtitleTopPrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //             //   "subtitleTop": "AI will start a conversation with this message.",
      //             //   "placeholder": "Hello, how can I help you today?",
      //             //   "validators": "maxLength:1024",
      //             // },
      //             // "conversation22": {
      //             //   "value": "abcdefthgđdfh",
      //             //   "returnProperties": ["value", "group", "variableName", "valueType"],
      //             //   "group": "foobar",
      //             //   "variableName": "conversation",
      //             //   "valueType": "object",
      //             //   "type": "conversation",
      //             //   "wrap": false,
      //             //   "title": "Only message preview - NO wrapping, edit, buttons or actions",
      //             //   "edit": false,
      //             //   "subtitleTopPrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //             //   "subtitleTop": "AI will start a conversation with this message.",
      //             //   "contactMessage": "Requested info",
      //             //   "placeholder": "Hello, how can I help you today?",
      //             //   "validators": "maxLength:1024",
      //             // },
      //             // "conversation2": {
      //             //   "value": "abcdefthgđdfh",
      //             //   "returnProperties": ["value", "group", "variableName", "valueType"],
      //             //   "group": "foobar",
      //             //   "variableName": "conversation",
      //             //   "valueType": "object",
      //             //   "type": "conversation",
      //             //   "wrap": true,
      //             //   "title": "NO edit but buttons available",
      //             //   "edit": true,
      //             //   "subtitleTopPrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //             //   "subtitleTop": "AI will start a conversation with this message.",
      //             //   "placeholder": "Hello, how can I help you today?",
      //             //   "validators": "maxLength:1024",
      //             //   "buttons": {
      //             //     "value": [],
      //             //     "returnProperties": ["value", "group", "variableName", "valueType"],
      //             //     "group": "foobar",
      //             //     "variableName": "...",
      //             //     "valueType": "string",
      //             //     "type": "message-buttons",
      //             //     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //             //     "addButtonText": "Add button",
      //             //     "minButtons": 0,
      //             //     "maxButtons": 10,
      //             //     "enter": "Add",
      //             //     "blackButtons": true,
      //             //     "validators": "required|maxLength:20",
      //             //     "validatorMessages": {
      //             //       "required": "Button text is required.",          
      //             //       "maxLength": "Please limit your text to {{validator}} characters."      
      //             //     },
      //             //   },
      //             // },
      //             // "conversation3": {
      //             //   "value": "abcdefthgđdfh",
      //             //   "returnProperties": ["value", "group", "variableName", "valueType"],
      //             //   "group": "foobar",
      //             //   "variableName": "conversation",
      //             //   "valueType": "object",
      //             //   "type": "conversation",
      //             //   "wrap": true,
      //             //   "title": "Edit and actions available",
      //             //   "edit": true,
      //             //   "subtitleTopPrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //             //   "subtitleTop": "AI will start a conversation with this message.",
      //             //   "placeholder": "Hello, how can I help you today?",
      //             //   "validators": "maxLength:1024",
      //             //   "actions": {
      //             //       "value": [
      //             //         { "value": 1 }
      //             //       ],
      //             //       "type": "action-buttons",
      //             //       "options": [
      //             //         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //             //         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //             //         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //             //       ],
      //             //       "addActionIcon": "SBIconList.PLUS",
      //             //       "addActionText": "Add action",
      //             //       "returnKey": "responseActions"
      //             //     }
      //             // },
      //           },
      //         },
      //       },
      //       {
      //         "title": "222 Role and communication style",
      //         "subtitle": "Select your AI assistant's role and customize its communication style.",
      //         "icon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //         "formSchema": {
      //           "type": "object",
      //           "properties": {
      //             "role": {
      //               "value": 1,
      //               "type": "dropdown",
      //               "title": "Role",
      //               "validators": "required",
      //               "options": [
      //                 { "value": 1, "label": "Customer support", "color": "#f8ffff", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg" },
      //                 { "value": 2, "label": "Sales", "color": "#fff8f8", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" },
      //                 { "value": 3, "label": "Lead qualification", "color": "#faf8ff", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_always_gray.svg" },
      //                 { "value": 4, "label": "Custom", "color": "#f8ffff", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg" }
      //               ]
      //             },
      //             "roleForm": {
      //               "showIf": {
      //                 "input": "role",
      //                 "value": [1]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "anyQuestion": {
      //                   "type": "dropdown",
      //                   "title": "When a contact asks any question",
      //                   "validators": "required",
      //                   "options": [
      //                     { "value": 1, "label": "Look for an answer in the provided data" }
      //                   ]
      //                 },
      //                 "urlInput": {
      //                   "showIf": {
      //                     "input": "anyQuestion",
      //                     "value": 1
      //                   },
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "url-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check web scraping tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Connect website",
      //                   "populatedText": ["url", "pathList"],
      //                   "returnKey": "providedUrls",
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "validOneOfMessage": "Add at least one data source.",
      //                   "countFields": ["includePaths", "excludePaths1"],
      //                   "modal": {
      //                     "title": "Connect website",
      //                     "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                     "subtitle": "Learn more about web scraping and get helpful tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                     "schema": {
      //                       "type": "object",
      //                       "properties": {
      //                         "radio": {
      //                           "value": 1,
      //                           "type": "radio-NMBR",
      //                           "validators": "required",
      //                           "options": [
      //                             { "value": 1, "label": "Crawl entire website", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" },
      //                             { "value": 2, "label": "Crawl specific links", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" }
      //                           ]
      //                         },
      //                         "divider": {
      //                           "type": "divider"
      //                         },
      //                         "url": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "input",
      //                           "title": "Website URL",
      //                           "placeholder": "https://website.com",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Crawl all available pages starting with this URL.",
      //                           "validators": "url",
      //                           "hideOptional": true
      //                         },
      //                         "title": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "group-title",
      //                           "title": "Filter options"
      //                         },
      //                         "includePaths": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Include only specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Only paths or pages you specify here will be crawled (e.g., products/* for all pages under \"products\" or products/product-name.html for a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., products/* or products/product-name.html",
      //                           "validators": "urlPath",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "enter": "Add",
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "includePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "excludePaths1": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Exclude specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under \"blog\" or blog/article-name.html to exclude a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., blog/* or blog/article-name.html",
      //                           "validators": "urlPath",
      //                           "enter": "Add",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "excludePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "pathList": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 2
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "List of URLs",
      //                           "subtitle": "Only links you specify here will be crawled.",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add link",
      //                           "placeholder": "https://website.com/about.html",
      //                           "validators": "required|url",
      //                           "validatorMessages": {
      //                             "required": "Url is required."
      //                           },
      //                           "enter": "Add", 
      //                           "minButtons": 1,
      //                           "maxButtons": 100,
      //                           "returnKey": "urlList"
      //                         },
      //                         "divider2": {
      //                           "type": "divider"
      //                         },
      //                         "rescrape": {
      //                           "type": "dropdown",
      //                           "title": "Rescrape pages",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Define how often added pages should be rescraped.",
      //                           "validators": "required",
      //                           "value": 4,
      //                           "options": [
      //                             { "value": 1, "label": "Never" },
      //                             { "value": 2, "label": "Every day" },
      //                             { "value": 3, "label": "Every week" },
      //                             { "value": 4, "label": "Every month" }
      //                           ]
      //                         }
      //                       }
      //                     }
      //                   }
      //                 },
      //                 "fileInput": {
      //                   "showIf": {
      //                     "input": "anyQuestion",
      //                     "value": 1
      //                   },
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "file-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check how to properly format files <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Add files...",
      //                   "allowedFileTypes": "doc, docx, html, md, pdf, pptx, txt",
      //                   "minFiles": 0,
      //                   "maxFiles": 1000,
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "returnKey": "providedFiles"
      //                 },
      //                 "conversation": {
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "And then respond in the following format",
      //                   "edit": false,
      //                   "placeholder": "Hello, how can I help you today?",
      //                   "validators": "required|maxLength:1024",
      //                   "buttons": {
      //                     "value": [
      //                       { "title": "👍 That was helpful" },
      //                       { "title": "Continue chat" }
      //                     ],
      //                     "returnProperties": ["value", "group", "variableName", "valueType"],
      //                     "group": "foobar",
      //                     "variableName": "...",
      //                     "valueType": "string",
      //                     "type": "message-buttons",
      //                     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                     "addButtonText": "Add button",
      //                     "minButtons": 0,
      //                     "maxButtons": 10,
      //                     "enter": "Add",
      //                     "blackButtons": true,
      //                     "validators": "required|maxLength:20",
      //                     "validatorMessages": {
      //                       "required": "Button text is required.",          
      //                       "maxLength": "Please limit your text to {{validator}} characters."      
      //                     },
      //                   },
      //                 },
      //               }
      //             },
      //             "roleForm2": {
      //               "showIf": {
      //                 "input": "role",
      //                 "value": [2]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "response": {
      //                   "type": "dropdown",
      //                   "title": "When a contact asks any question",
      //                   "validators": "required",
      //                   "options": [
      //                     { "value": 1, "label": "Look for an answer in the provided data" }
      //                   ]
      //                 },
      //                 "urlInput": {
      //                   "showIf": {
      //                     "input": "response",
      //                     "value": 1
      //                   },
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "url-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check web scraping tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Connect website",
      //                   "populatedText": ["url", "pathList"],
      //                   "returnKey": "providedUrls",
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "validOneOfMessage": "Add at least one data source.",
      //                   "countFields": ["includePaths", "excludePaths1"],
      //                   "modal": {
      //                     "title": "Connect website",
      //                     "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                     "subtitle": "Learn more about web scraping and get helpful tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                     "schema": {
      //                       "type": "object",
      //                       "properties": {
      //                         "radio": {
      //                           "value": 1,
      //                           "type": "radio-NMBR",
      //                           "validators": "required",
      //                           "options": [
      //                             { "value": 1, "label": "Crawl entire website", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" },
      //                             { "value": 2, "label": "Crawl specific links", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" }
      //                           ]
      //                         },
      //                         "divider": {
      //                           "type": "divider"
      //                         },
      //                         "url": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "input",
      //                           "title": "Website URL",
      //                           "placeholder": "https://website.com",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Crawl all available pages starting with this URL.",
      //                           "validators": "url",
      //                           "hideOptional": true
      //                         },
      //                         "title": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "group-title",
      //                           "title": "Filter options"
      //                         },
      //                         "includePaths": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Include only specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Only paths or pages you specify here will be crawled (e.g., products/* for all pages under \"products\" or products/product-name.html for a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., products/* or products/product-name.html",
      //                           "validators": "urlPath",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "enter": "Add",
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "includePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "excludePaths1": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Exclude specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under \"blog\" or blog/article-name.html to exclude a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., blog/* or blog/article-name.html",
      //                           "validators": "urlPath",
      //                           "enter": "Add",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "excludePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "pathList": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 2
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "List of URLs",
      //                           "subtitle": "Only links you specify here will be crawled.",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add link",
      //                           "placeholder": "https://website.com/about.html",
      //                           "validators": "required|url",
      //                           "validatorMessages": {
      //                             "required": "Url is required."
      //                           },
      //                           "enter": "Add", 
      //                           "minButtons": 1,
      //                           "maxButtons": 100,
      //                           "returnKey": "urlList"
      //                         },
      //                         "divider2": {
      //                           "type": "divider"
      //                         },
      //                         "rescrape": {
      //                           "type": "dropdown",
      //                           "title": "Rescrape pages",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Define how often added pages should be rescraped.",
      //                           "validators": "required",
      //                           "value": 4,
      //                           "options": [
      //                             { "value": 1, "label": "Never" },
      //                             { "value": 2, "label": "Every day" },
      //                             { "value": 3, "label": "Every week" },
      //                             { "value": 4, "label": "Every month" }
      //                           ]
      //                         }
      //                       }
      //                     }
      //                   }
      //                 },
      //                 "fileInput": {
      //                   "showIf": {
      //                     "input": "response",
      //                     "value": 1
      //                   },
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "file-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check how to properly format files <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Add files...",
      //                   "allowedFileTypes": "doc, docx, html, md, pdf, pptx, txt",
      //                   "minFiles": 0,
      //                   "maxFiles": 1000,
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "returnKey": "providedFiles"
      //                 },
      //                 "conversation": {
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "And then respond in the following format",
      //                   "edit": false,
      //                   "validators": "required|maxLength:1024",
      //                   "buttons": {
      //                     "returnProperties": ["value", "group", "variableName", "valueType"],
      //                     "group": "foobar",
      //                     "variableName": "...",
      //                     "valueType": "string",
      //                     "type": "message-buttons",
      //                     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                     "addButtonText": "Add button",
      //                     "minButtons": 0,
      //                     "maxButtons": 10,
      //                     "enter": "Add",
      //                     "blackButtons": true,
      //                     "validators": "required|maxLength:20",
      //                     "validatorMessages": {
      //                       "required": "Button text is required.",          
      //                       "maxLength": "Please limit your text to {{validator}} characters."      
      //                     },
      //                   },
      //                 },
      //                 "divider": {
      //                   "type": "dividerFull"
      //                 },
      //                 "userWants": {
      //                   "type": "input",
      //                   "value": "Purchase a product or service",
      //                   "title": "When a contact wants to",
      //                   "placeholder": "Purchase a product or service",
      //                   "errorMessage": "Schema error message",
      //                   "validatorMessages": {
      //                     "required": "Field is required."
      //                   },
      //                   "validators": "required|minLength:20|maxLength:100",
      //                   "warningValidators": "minLength"
      //                 },
      //                 "role": {
      //                   "value": 1,
      //                   "type": "dropdown",
      //                   "title": "Role",
      //                   "validators": "required",
      //                   "options": [
      //                     { "value": 1, "label": "Collect info from the contact" }
      //                   ]
      //                 },
      //                 "message": {
      //                   "type": "textarea",
      //                   "title": "",
      //                   "placeholder": "Product or service name, full name, and address.",
      //                   "validators": "maxLength:1024",
      //                   "child": true
      //                 }
      //               }
      //             },
      //             "roleForm3": {
      //               "showIf": {
      //                 "input": "role",
      //                 "value": [3]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "response": {
      //                   "type": "dropdown",
      //                   "title": "When a contact asks any question",
      //                   "validators": "required",
      //                   "options": [
      //                     { "value": 1, "label": "Look for an answer in the provided data" }
                          
                          
      //                   ]
      //                 },
      //                 "urlInput": {
      //                   "showIf": {
      //                     "input": "response",
      //                     "value": 1
      //                   },
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "url-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check web scraping tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Connect website",
      //                   "populatedText": ["url", "pathList"],
      //                   "returnKey": "providedUrls",
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "validOneOfMessage": "Add at least one data source.",
      //                   "countFields": ["includePaths", "excludePaths1"],
      //                   "modal": {
      //                     "title": "Connect website",
      //                     "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                     "subtitle": "Learn more about web scraping and get helpful tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                     "schema": {
      //                       "type": "object",
      //                       "properties": {
      //                         "radio": {
      //                           "value": 1,
      //                           "type": "radio-NMBR",
      //                           "validators": "required",
      //                           "options": [
      //                             { "value": 1, "label": "Crawl entire website", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" },
      //                             { "value": 2, "label": "Crawl specific links", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" }
      //                           ]
      //                         },
      //                         "divider": {
      //                           "type": "divider"
      //                         },
      //                         "url": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "input",
      //                           "title": "Website URL",
      //                           "placeholder": "https://website.com",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Crawl all available pages starting with this URL.",
      //                           "validators": "url",
      //                           "hideOptional": true
      //                         },
      //                         "title": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "group-title",
      //                           "title": "Filter options"
      //                         },
      //                         "includePaths": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Include only specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Only paths or pages you specify here will be crawled (e.g., products/* for all pages under \"products\" or products/product-name.html for a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., products/* or products/product-name.html",
      //                           "validators": "urlPath",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "enter": "Add",
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "includePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "excludePaths1": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Exclude specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under \"blog\" or blog/article-name.html to exclude a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., blog/* or blog/article-name.html",
      //                           "validators": "urlPath",
      //                           "enter": "Add",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "excludePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "pathList": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 2
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "List of URLs",
      //                           "subtitle": "Only links you specify here will be crawled.",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add link",
      //                           "placeholder": "https://website.com/about.html",
      //                           "validators": "required|url",
      //                           "validatorMessages": {
      //                             "required": "Url is required."
      //                           },
      //                           "enter": "Add", 
      //                           "minButtons": 1,
      //                           "maxButtons": 100,
      //                           "returnKey": "urlList"
      //                         },
      //                         "divider2": {
      //                           "type": "divider"
      //                         },
      //                         "rescrape": {
      //                           "type": "dropdown",
      //                           "title": "Rescrape pages",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Define how often added pages should be rescraped.",
      //                           "validators": "required",
      //                           "value": 4,
      //                           "options": [
      //                             { "value": 1, "label": "Never" },
      //                             { "value": 2, "label": "Every day" },
      //                             { "value": 3, "label": "Every week" },
      //                             { "value": 4, "label": "Every month" }
      //                           ]
      //                         }
      //                       }
      //                     }
      //                   }
      //                 },
      //                 "fileInput": {
      //                   "showIf": {
      //                     "input": "response",
      //                     "value": 1
      //                   },
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "file-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check how to properly format files <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Add files...",
      //                   "allowedFileTypes": "doc, docx, html, md, pdf, pptx, txt",
      //                   "minFiles": 0,
      //                   "maxFiles": 1000,
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "returnKey": "providedFiles"
      //                 },
      //                 "conversation": {
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "And then respond in the following format",
      //                   "edit": false,
      //                   "validators": "required|maxLength:1024",
      //                   "buttons": {
      //                     "returnProperties": ["value", "group", "variableName", "valueType"],
      //                     "group": "foobar",
      //                     "variableName": "...",
      //                     "valueType": "string",
      //                     "type": "message-buttons",
      //                     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                     "addButtonText": "Add button",
      //                     "minButtons": 0,
      //                     "maxButtons": 10,
      //                     "enter": "Add",
      //                     "blackButtons": true,
      //                     "validators": "required|maxLength:20",
      //                     "validatorMessages": {
      //                       "required": "Button text is required.",          
      //                       "maxLength": "Please limit your text to {{validator}} characters."      
      //                     },
      //                   },
      //                 },
      //                 "divider": {
      //                   "type": "dividerFull"
      //                 },
      //                 "question": {
      //                   "type": "input",
      //                   "title": "When a contact wants to",
      //                   "value": "Purchase a product or service",
      //                   "placeholder": "Purchase a product or service",
      //                   "validators": "required|minLength:20|maxLength:100",
      //                   "warningValidators": "minLength"
      //                 },
      //                 "then": {
      //                   "type": "dropdown",
      //                   "title": "Then",
      //                   "validators": "required",
      //                   "options": [
      //                     { "value": 1, "label": "Collect info by asking questions" }
      //                   ]
      //                 },
      //                 "questions": {
      //                   "type": "questions",
                        
      //                   "addQuestionIcon": "SBIconList.PLUS",
      //                   "addQuestionText": "Add question",
      //                   "minQuestions": 1,
      //                   "maxQuestions": 10,
      //                   "minDefaultQuestions": 1,
      //                   "placeholder": "E.g., What is your full name and address?",
                        
      //                   "child": true,
      //                   "returnKey": "questionsList",
      //                   "validators": "required|maxLength:100",
      //                   "validatorMessages": {
      //                     "required": "Question text is required."          
      //                   }
      //                 },
      //                 "divider2": {
      //                   "type": "dividerFull"
      //                 },
      //                 "additionalQuestions": {
      //                   "type": "questions-list-modal",
      //                   "title": "Additional conditional questions",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Add a group of questions that will be asked if previous answers met defined rule.",
      //                   "addQuestionIcon": "SBIconList.PLUS",
      //                   "addQuestionText": "Add group of questions",
      //                   "minQuestions": 0,
      //                   "maxQuestions": 10,
      //                   "placeholder": "E.g., What is your full name and address?",
      //                   "returnKey": "questionsList"
      //                 }
      //               }
      //             },
      //             "roleForm4": {
      //               "showIf": {
      //                 "input": "role",
      //                 "value": [4]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "question": {
      //                   "type": "input",
      //                   "title": "Role",
      //                   "value": "",
      //                   "placeholder": "E.g., Appointment scheduler",
      //                   "validators": "required|minLength:20|maxLength:100",
      //                   "warningValidators": "minLength"
      //                 },
      //                 "title": {
      //                   "type": "group-title",
      //                   "title": "Knowledge base"
      //                 },
      //                 "urlInput": {
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "url-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check web scraping tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Connect website",
      //                   "populatedText": ["url", "pathList"],
      //                   "returnKey": "providedUrls",
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "validOneOfMessage": "Add at least one data source.",
      //                   "countFields": ["includePaths", "excludePaths1"],
      //                   "modal": {
      //                     "title": "Connect website",
      //                     "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                     "subtitle": "Learn more about web scraping and get helpful tips <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                     "schema": {
      //                       "type": "object",
      //                       "properties": {
      //                         "radio": {
      //                           "value": 1,
      //                           "type": "radio-NMBR",
      //                           "validators": "required",
      //                           "options": [
      //                             { "value": 1, "label": "Crawl entire website", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" },
      //                             { "value": 2, "label": "Crawl specific links", "image": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg" }
      //                           ]
      //                         },
      //                         "divider": {
      //                           "type": "divider"
      //                         },
      //                         "url": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "input",
      //                           "title": "Website URL",
      //                           "placeholder": "https://website.com",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Crawl all available pages starting with this URL.",
      //                           "validators": "url",
      //                           "hideOptional": true
      //                         },
      //                         "title": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "group-title",
      //                           "title": "Filter options"
      //                         },
      //                         "includePaths": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Include only specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Only paths or pages you specify here will be crawled (e.g., products/* for all pages under \"products\" or products/product-name.html for a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., products/* or products/product-name.html",
      //                           "validators": "urlPath",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "enter": "Add",
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "includePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "excludePaths1": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 1
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "Exclude specific paths or pages",
      //                           "titleType": "normal",
      //                           "subtitle": "Any path or page you specify here will be excluded from the crawl (e.g., blog/* to exclude all pages under \"blog\" or blog/article-name.html to exclude a specific page).",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add path or page",
      //                           "placeholder": "E.g., blog/* or blog/article-name.html",
      //                           "validators": "urlPath",
      //                           "enter": "Add",
      //                           "warningValidators": "",
      //                           "validatorMessages": {
      //                             "required": "Path is required.",
      //                             "minLength": "Add descriptive info to improve the AI's responses.",
      //                             "urlPath": "Invalid format. Enter only one valid path or page, such as products/* or products/product-name.html"
      //                           },
      //                           "minButtons": 1,
      //                           "maxButtons": 10,
      //                           "returnKey": "excludePaths",
      //                           "bottomSpacer": 3
      //                         },
      //                         "pathList": {
      //                           "showIf": {
      //                             "input": "radio",
      //                             "value": 2
      //                           },
      //                           "type": "message-buttons",
      //                           "title": "List of URLs",
      //                           "subtitle": "Only links you specify here will be crawled.",
      //                           "addButtonIcon": "SBIconList.PLUS",
      //                           "addButtonText": "Add link",
      //                           "placeholder": "https://website.com/about.html",
      //                           "validators": "required|url",
      //                           "validatorMessages": {
      //                             "required": "Url is required."
      //                           },
      //                           "enter": "Add", 
      //                           "minButtons": 1,
      //                           "maxButtons": 100,
      //                           "returnKey": "urlList"
      //                         },
      //                         "divider2": {
      //                           "type": "divider"
      //                         },
      //                         "rescrape": {
      //                           "type": "dropdown",
      //                           "title": "Rescrape pages",
      //                           "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                           "subtitle": "Define how often added pages should be rescraped.",
      //                           "validators": "required",
      //                           "value": 4,
      //                           "options": [
      //                             { "value": 1, "label": "Never" },
      //                             { "value": 2, "label": "Every day" },
      //                             { "value": 3, "label": "Every week" },
      //                             { "value": 4, "label": "Every month" }
      //                           ]
      //                         }
      //                       }
      //                     }
      //                   }
      //                 },
      //                 "fileInput": {
      //                   "child": true,
      //                   "width": 50,
      //                   "value": [],
      //                   "type": "file-input-wrapper",
      //                   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                   "subtitle": "Check how to properly format files <a href=\"http://www.google.com\" target=\"_blank\">here</a>.",
      //                   "addButtonText": "Add files...",
      //                   "allowedFileTypes": "doc, docx, html, md, pdf, pptx, txt",
      //                   "minFiles": 0,
      //                   "maxFiles": 1000,
      //                   "validOneOf": ["fileInput", "urlInput"],
      //                   "returnKey": "providedFiles"
      //                 },
      //                 "message": {
      //                   "type": "textarea",
      //                   "title": "Custom instruction",
      //                   "height": 456,                       
      //                   "placeholder": "E.g., Your goal is to make the process of booking, rescheduling, or canceling appointments as smooth and efficient as possible for the user. Here are the detailed instructions to guide you in fulfilling your role effectively:\n\n1. Gather Appointment Details\n\nPurpose of Appointment: Ask what the appointment is for (e.g., consultation, meeting, service, etc.).\n\nPreferred Date and Time: Inquire about their preferred date(s) and time(s).\n\nAvailability Confirmation: Check for available slots and provide a few options if the user's preferred time is not available…",
      //                   "validators": "required|minLength:500|maxLength:5000",
      //                   "warningValidators": "minLength"
      //                 }
      //               }
      //             },
      //             "success": {
      //               "showIf": {
      //                 "input": "role",
      //                 "value": [1]
      //               },
      //               "color": "default",
      //               "type": "object",
      //               "title": "👍 When you conclude the contact was assisted",
      //               "hideOptional": true,
      //               "properties": {
      //                 "conversation3": {
      //                   "value": "I'm glad I could help today. We'll close the chat now, but feel free to reach out if you have more questions.",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Respond in the following format",
      //                   "edit": true,
      //                   "placeholder": "I'm glad I could help today. We'll close the chat now, but feel free to reach out if you have more questions.",
      //                   "contactMessage": "Message indicating successful assistance",
      //                   "validators": "required|maxLength:1024",
      //                   "actions": {
      //                       "value": [
      //                         { "value": 1 }
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //               }
      //             },
      //             "success2": {
      //               "showIf": {
      //                 "input": "role",
      //                 "value": [2]
      //               },
      //               "color": "default",
      //               "type": "object",
      //               "title": "👍 When you've collected info from the contact",
      //               "hideOptional": true,
      //               "properties": {
      //                 "conversation3": {
      //                   "value": "Thanks for providing the information! Our sales team will contact you shortly to assist you further. We'll close the chat now, but feel free to reach out if you have more questions.",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Respond in the following format",
      //                   "edit": true,
      //                   "placeholder": "Thanks for providing the information! Our sales team will contact you shortly to assist you further. We'll close the chat now, but feel free to reach out if you have more questions.",
      //                   "contactMessage": "Requested info",
      //                   "validators": "required|maxLength:1024",
      //                   "actions": {
      //                       "value": [
      //                         { "value": 1 },
      //                         { "value": 3 }
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //               }
      //             },
      //             "success3": {
      //               "showIf": {
      //                 "input": "role",
      //                 "value": [3]
      //               },
      //               "color": "default",                  
      //               "type": "object",
      //               "title": "👍 When you've collected info from the contact",
      //               "hideOptional": true,
      //               "properties": {
      //                 "conversation3": {
      //                   "value": "Thanks for providing the information! Our sales team will contact you shortly to assist you further. We'll close the chat now, but feel free to reach out if you have more questions.",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Respond in the following format",
      //                   "edit": true,
      //                   "placeholder": "Thanks for providing the information! Our sales team will contact you shortly to assist you further. We'll close the chat now, but feel free to reach out if you have more questions.",
      //                   "contactMessage": "Requested info",
      //                   "validators": "required|maxLength:1024",
      //                   "actions": {
      //                       "value": [
      //                         { "value": 1 },
      //                         { "value": 3 }
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //               }
      //             }
      //           }
      //         }
      //       },
      //       {
      //         "title": "333 Human handoff",
      //         "subtitle": "Define how your AI assistant should transfer chats to human agents.",
      //         "icon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //         "formSchema": {
      //           "type": "object",
      //           "properties": {
      //             "condition1": {
      //               "type": "dropdown",
      //               "title": "🧐 If you cannot provide an answer to the contact",
      //               "validators": "required",
      //               "options": [
      //                 { "value": 1, "label": "Assign chat to the support team" },
      //                 { "value": 2, "label": "Collect info and assign chat to the support team" }
      //               ]
      //             },
      //             "assign": {
      //               "showIf": {
      //                 "input": "condition1",
      //                 "value": [1]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "conversation": {
      //                   "value": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Send a message to confirm assignment",
      //                   "edit": true,
      //                   "placeholder": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                   "validators": "required|maxLength:1024",
      //                   "buttons": {
      //                     "value": [
      //                       { "title": "👍 Yes", "fixed": true },
      //                       { "title": "Continue chat", "fixed": true }
      //                     ],
      //                     "returnProperties": ["value", "group", "variableName", "valueType"],
      //                     "group": "foobar",
      //                     "variableName": "...",
      //                     "valueType": "string",
      //                     "type": "message-buttons",
      //                     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                     "addButtonText": "Add button",
      //                     "minButtons": 0,
      //                     "maxButtons": 2,
      //                     "enter": "Add",
      //                     "blackButtons": true,
      //                     "validators": "required|maxLength:20",
      //                     "validatorMessages": {
      //                       "required": "Button text is required.",          
      //                       "maxLength": "Please limit your text to {{validator}} characters."      
      //                     },
      //                   },
      //                 },
      //                 // "message": {
      //                 //   "type": "textarea",
      //                 //   "title": "Send a message to confirm assignment",
      //                 //   "placeholder": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                 //   "value": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                 //   "validators": "required|minLength:20|maxLength:1024",
      //                 //   "warningValidators": "minLength"
      //                 // },
      //                 // "buttons": {
      //                 //   "child": true,
      //                 //   "value": [
      //                 //     { "title": "👍 Yes", "fixed": true },
      //                 //     { "title": "Continue chat", "fixed": true }
      //                 //   ],
      //                 //   "type": "message-buttons",
      //                 //   "addButtonIcon": "SBIconList.PLUS",
      //                 //   "addButtonText": "Add button",
      //                 //   "minButtons": 0,
      //                 //   "maxButtons": 2,
      //                 //   "returnKey": "responseButtons",
      //                 //   "enter": "Add"
      //                 // },
      //                 "dividerFull": {
      //                   "type": "dividerFull"
      //                 },
      //                 "positiveResponse": {
      //                   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "If the response is positive then send a message",
      //                   "edit": true,
      //                   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "contactMessage": "Yes",
      //                   "validators": "required|maxLength:1024",
      //                   "actions": {
      //                       "value": [
      //                         { "value": 2 },
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //                 // "positiveResponse": {
      //                 //   "type": "textarea",
      //                 //   "title": "If the response is positive then send a message",
      //                 //   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "validators": "required",
      //                 //   "validatorMessages": {
      //                 //     "required": "Message is required."
      //                 //   }
      //                 // },
      //                 // "actionsTest": {
      //                 //   "value": [
      //                 //     { "value": 2 }
      //                 //   ],
      //                 //   "type": "action-buttons",
      //                 //   "options": [
      //                 //     { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                 //     { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                 //     { "value": 3, "label": "Send chat summary to the email", "type": "email", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                 //   ],
      //                 //   "addActionIcon": "SBIconList.PLUS",
      //                 //   "addActionText": "Add action",
      //                 //   "returnKey": "responseActions"
      //                 // },
      //                 "divider": {
      //                   "type": "divider"
      //                 },
      //                 "negativeResponse": {
      //                   "value": "AI-generated message for continuing the chat",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "If the contact's response is negative",
      //                   "edit": false,
      //                   "placeholder": "Hello, how can I help you today?",
      //                   "contactMessage": "Continue chat",
      //                   "validators": "required",
      //                 },
      //                 // "question": {
      //                 //   "type": "input",
      //                 //   "title": "If the contact's response is negative",
      //                 //   "value": "Continue chatting",
      //                 //   "validators": "required",
      //                 //   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                 //   "subtitle": "AI will keep chatting with the contact."
      //                 // }
      //               }
      //             },
      //             "collect": {
      //               "showIf": {
      //                 "input": "condition1",
      //                 "value": [2]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "conversation": {
      //                   "value": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Send a message to confirm assignment",
      //                   "edit": true,
      //                   "placeholder": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                   "validators": "required|maxLength:1024",
      //                   "buttons": {
      //                     "value": [
      //                       { "title": "👍 Yes", "fixed": true },
      //                       { "title": "Continue chat", "fixed": true }
      //                     ],
      //                     "returnProperties": ["value", "group", "variableName", "valueType"],
      //                     "group": "foobar",
      //                     "variableName": "...",
      //                     "valueType": "string",
      //                     "type": "message-buttons",
      //                     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                     "addButtonText": "Add button",
      //                     "minButtons": 0,
      //                     "maxButtons": 2,
      //                     "enter": "Add",
      //                     "blackButtons": true,
      //                     "validators": "required|maxLength:20",
      //                     "validatorMessages": {
      //                       "required": "Button text is required.",          
      //                       "maxLength": "Please limit your text to {{validator}} characters."      
      //                     },
      //                   },
      //                 },
      //                 // "message": {
      //                 //   "type": "textarea",
      //                 //   "title": "Send a message to confirm assignment",
      //                 //   "placeholder": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                 //   "value": "Hmm, I don't have the information you're looking for. Would you like me to connect you with our support team for further assistance?",
      //                 //   "validators": "required|minLength:20|maxLength:1024",
      //                 //   "warningValidators": "minLength"
      //                 // },
      //                 // "buttons": {
      //                 //   "child": true,
      //                 //   "value": [
      //                 //     { "title": "👍 Yes", "fixed": true },
      //                 //     { "title": "Continue chat", "fixed": true }
      //                 //   ],
      //                 //   "type": "message-buttons",
      //                 //   "addButtonIcon": "SBIconList.PLUS",
      //                 //   "addButtonText": "Add button",
      //                 //   "minButtons": 0,
      //                 //   "maxButtons": 2,
      //                 //   "returnKey": "responseButtons",
      //                 //   "enter": "Add"
      //                 // },
      //                 "dividerFull": {
      //                   "type": "dividerFull"
      //                 },
      //                 "positiveResponse": {
      //                   "type": "textarea",
      //                   "title": "If the response is positive collect info",
      //                   "placeholder": "Full name, email, and issue description.",
      //                   "validators": "required",
      //                   "validatorMessages": {
      //                     "required": "Message is required."
      //                   }
      //                 },
      //                 "positiveResponse2": {
      //                   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "When you've collected info respond in the following format",
      //                   "edit": true,
      //                   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "contactMessage": "Requested info",
      //                   "validators": "required|maxLength:1024",
      //                   "actions": {
      //                       "value": [
      //                         { "value": 1 },
      //                         { "value": 3 },
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //                 // "positiveResponse2": {
      //                 //   "type": "textarea",
      //                 //   "title": "When you've collected info send a message",
      //                 //   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "validators": "required"
      //                 // },
      //                 // "actionsTest": {
      //                 //   "value": [
      //                 //     { "value": 2 },
      //                 //     { "value": 3 }
      //                 //   ],
      //                 //   "type": "action-buttons",
      //                 //   "options": [
      //                 //     { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                 //     { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                 //     { "value": 3, "label": "Send chat summary to the email", "type": "email", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                 //   ],
      //                 //   "addActionIcon": "SBIconList.PLUS",
      //                 //   "addActionText": "Add action",
      //                 //   "returnKey": "responseActions"
      //                 // },
      //                 "divider": {
      //                   "type": "divider"
      //                 },
      //                 "negativeResponse": {
      //                   "value": "AI-generated message for continuing the chat",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "If the response is negative respond in the following format",
      //                   "edit": false,
      //                   "placeholder": "Hello, how can I help you today?",
      //                   "contactMessage": "Continue chat",
      //                   "validators": "required",
      //                 },
      //                 // "question": {
      //                 //   "type": "input",
      //                 //   "title": "If the contact's response is negative",
      //                 //   "value": "Continue chatting",
      //                 //   "validators": "required",
      //                 //   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                 //   "subtitle": "AI will keep chatting with the contact."
      //                 // }
      //               }
      //             },
      //             "condition2": {
      //               "type": "dropdown",
      //               "title": "👥 If the contact requests to speak with the support team",
      //               "validators": "required",
      //               "options": [
      //                 { "value": 1, "label": "Assign chat to the support team" },
      //                 { "value": 2, "label": "Collect info and assign chat to the support team" }
      //               ]
      //             },
      //             "requestSupport": {
      //               "showIf": {
      //                 "input": "condition2",
      //                 "value": [1]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "conversation": {
      //                   "value": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Send a message to confirm assignment",
      //                   "edit": true,
      //                   "placeholder": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                   "validators": "required|maxLength:1024",
      //                   "buttons": {
      //                     "value": [
      //                       { "title": "👍 Yes", "fixed": true },
      //                       { "title": "Continue chat", "fixed": true }
      //                     ],
      //                     "returnProperties": ["value", "group", "variableName", "valueType"],
      //                     "group": "foobar",
      //                     "variableName": "...",
      //                     "valueType": "string",
      //                     "type": "message-buttons",
      //                     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                     "addButtonText": "Add button",
      //                     "minButtons": 0,
      //                     "maxButtons": 2,
      //                     "enter": "Add",
      //                     "blackButtons": true,
      //                     "validators": "required|maxLength:20",
      //                     "validatorMessages": {
      //                       "required": "Button text is required.",          
      //                       "maxLength": "Please limit your text to {{validator}} characters."      
      //                     },
      //                   },
      //                 },
      //                 // "message": {
      //                 //   "type": "textarea",
      //                 //   "title": "Send a message to confirm assignment",
      //                 //   "value": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                 //   "placeholder": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                 //   "validators": "required|maxLength:1024"
      //                 // },
      //                 // "buttons": {
      //                 //   "child": true,
      //                 //   "value": [
      //                 //     { "title": "👍 Yes", "fixed": true },
      //                 //     { "title": "Continue chat", "fixed": true }
      //                 //   ],
      //                 //   "type": "message-buttons",
      //                 //   "addButtonIcon": "SBIconList.PLUS",
      //                 //   "addButtonText": "Add button",
                        
                        
      //                 //   "maxButtons": 2,    
      //                 //   "returnKey": "responseButtons",
      //                 //   "enter": "Add"
      //                 // },
      //                 "dividerFull": {
      //                   "type": "dividerFull"
      //                 },
      //                 "positiveResponse": {
      //                   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "If the response is postive respond in the following format",
      //                   "edit": true,
      //                   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "contactMessage": "Yes",
      //                   "validators": "required|maxLength:1024",
      //                   "actions": {
      //                       "value": [
      //                         { "value": 2 },
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //                 // "message2": {
      //                 //   "type": "textarea",
      //                 //   "title": "If the response is positive send a message",
      //                 //   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "validators": "required"
      //                 // },
      //                 // "actionsTest": {
      //                 //   "value": [
      //                 //     { "value": 2 }
      //                 //   ],
      //                 //   "type": "action-buttons",
      //                 //   "options": [
      //                 //     { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                 //     { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                 //     { "value": 3, "label": "Send chat summary to the email", "type": "email", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                 //   ],
      //                 //   "addActionIcon": "SBIconList.PLUS",
      //                 //   "addActionText": "Add action",
      //                 //   "returnKey": "responseActions"
      //                 // },
      //                 "divider": {
      //                   "type": "divider"
      //                 },
      //                 "negativeResponse": {
      //                   "value": "AI-generated message for continuing the chat",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "If the contact's response is negative",
      //                   "edit": false,
      //                   "contactMessage": "Continue chat",
      //                   "validators": "required",
      //                 },
      //                 // "question": {
      //                 //   "type": "input",
      //                 //   "title": "If the response is negative",
      //                 //   "value": "Continue chatting",
      //                 //   "validators": "required",
      //                 //   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                 //   "subtitle": "AI will keep chatting with the contact."
      //                 // }
      //               }
      //             },
      //             "collect2": {
      //               "showIf": {
      //                 "input": "condition2",
      //                 "value": [2]
      //               },
      //               "type": "object",
      //               "child": true,
      //               "properties": {
      //                 "conversation": {
      //                   "value": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Send a message to confirm assignment",
      //                   "edit": true,
      //                   "placeholder": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                   "validators": "required|maxLength:1024",
      //                   "buttons": {
      //                     "value": [
      //                       { "title": "👍 Yes", "fixed": true },
      //                       { "title": "Continue chat", "fixed": true }
      //                     ],
      //                     "returnProperties": ["value", "group", "variableName", "valueType"],
      //                     "group": "foobar",
      //                     "variableName": "...",
      //                     "valueType": "string",
      //                     "type": "message-buttons",
      //                     "addButtonIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                     "addButtonText": "Add button",
      //                     "minButtons": 0,
      //                     "maxButtons": 2,
      //                     "enter": "Add",
      //                     "blackButtons": true,
      //                     "validators": "required|maxLength:20",
      //                     "validatorMessages": {
      //                       "required": "Button text is required.",          
      //                       "maxLength": "Please limit your text to {{validator}} characters."      
      //                     },
      //                   },
      //                 },
      //                 // "message": {
      //                 //   "type": "textarea",
      //                 //   "title": "Send a message to confirm assignment",
      //                 //   "value": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                 //   "placeholder": "I'd be happy to connect you with our support team. Could you please confirm if you'd like to proceed?",
      //                 //   "validators": "required|maxLength:1024"
      //                 // },
      //                 // "buttons": {
      //                 //   "child": true,
      //                 //   "value": [
      //                 //     { "title": "👍 Yes 555555", "fixed": true },
      //                 //     { "title": "Continue chat", "fixed": true }
      //                 //   ],
      //                 //   "type": "message-buttons",
      //                 //   "addButtonIcon": "SBIconList.PLUS",
      //                 //   "addButtonText": "Add button",
      //                 //   "returnKey": "responseButtons"
      //                 // },
      //                 "dividerFull": {
      //                   "type": "dividerFull"
      //                 },
      //                 "message1": {
      //                   "type": "textarea",
      //                   "title": "If the response is positive collect info",
      //                   "placeholder": "Full name, email, and issue description.",
      //                   "validators": "required"
      //                 },
      //                 "positiveResponse": {
      //                   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "When you’ve collected info respond in the following format",
      //                   "edit": true,
      //                   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                   "contactMessage": "Requested info",
      //                   "validators": "required|maxLength:1024",
      //                   "actions": {
      //                       "value": [
      //                         { "value": 1 },
      //                         { "value": 3 },
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //                 // "message2": {
      //                 //   "type": "textarea",
      //                 //   "title": "When you've collected info send a message",
      //                 //   "placeholder": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "value": "Thanks! Our support team will contact you shortly to assist you further.",
      //                 //   "validators": "required"
      //                 // },
      //                 // "actionsTest": {
      //                 //   "value": [
      //                 //     { "value": 2 },
      //                 //     { "value": 3 }
      //                 //   ],
      //                 //   "type": "action-buttons",
      //                 //   "options": [
      //                 //     { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                 //     { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                 //     { "value": 3, "label": "Send chat summary to the email", "type": "email", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                 //   ],
      //                 //   "addActionIcon": "SBIconList.PLUS",
      //                 //   "addActionText": "Add action",
      //                 //   "returnKey": "responseActions"
      //                 // },
      //                 "divider": {
      //                   "type": "divider"
      //                 },
      //                 "negativeResponse": {
      //                   "value": "AI-generated message for continuing the chat",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "If the response is negative respond in the following format",
      //                   "edit": false,
      //                   "contactMessage": "Continue chat",
      //                   "validators": "required",
      //                 },
      //                 // "question": {
      //                 //   "type": "input",
      //                 //   "title": "If the response is negative",
      //                 //   "value": "Continue chatting",
      //                 //   "validators": "required",
      //                 //   "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg",
      //                 //   "subtitle": "AI will keep chatting with the contact."
      //                 // }
      //               }
      //             }
      //           }
      //         }
      //       },
      //       {
      //         "title": "444 Fine-tuning and customization",
      //         "subtitle": "Provide instructions and actions to fine-tune your AI assistant's responses.",
      //         "icon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //         "formSchema": {
      //           "type": "object",
      //           "properties": {
      //             "unsupportedActions": {
      //               "value": "",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "...",
      //               "valueType": "string",
      //               "title": "Unsupported actions",
      //               "value": [
      //                 "Never book a call or in-person meeting."
      //               ],
      //               "type": "input-list",
      //               "subtitleTopPrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitleTop": "Add actions that the AI should avoid to prevent unwanted responses.",
      //               "placeholder": "Never book a call or in-person meeting.",
      //               "validators": "required",
      //               "validatorMessages": {
      //                 "required": "Field is required."
      //               },
      //               addButtonText: 'Add unsupported action',
      //             },
      //             "inPerson": {
      //               "value": "",
      //               "returnProperties": ["value", "group", "variableName", "valueType"],
      //               "group": "foobar",
      //               "variableName": "...",
      //               "valueType": "string",
      //               "title": "Custom instructions",
      //               "value": [
      //                 "If a contact asks an unclear question, politely ask for clarification."
      //               ],
      //               "type": "input-list",
      //               "subtitleTopPrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //               "subtitleTop": "Add specific guidelines to fine-tune the AI's responses.",
      //               "placeholder": "If a contact asks an unclear question, politely ask for clarification.",
      //               "validators": "required",
      //               "validatorMessages": {
      //                 "required": "Field is required."
      //               },
      //               addButtonText: 'Add custom instruction',
      //             },
      //             "followUp": {
      //               "type": "object",
      //               "title": "Follow-up actions",
      //               "properties": {
      //                 "duration": {
      //                   "value": "",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "...",
      //                   "valueType": "string",
      //                   "type": "duration",
      //                   "title": "If a chat is unresolved and a contact doesn't respond for",
      //                   "placeholder": "23",
      //                   "maxSeconds": 82800,
      //                   "maxSecondsMessage": "Waiting time can be up to 23 hours.",
      //                   "noDefault": true,
      //                   "hideOptional": true,
      //                   "hideScopes": ["days"]
      //                 },
      //                 "negativeResponse": {
      //                   "value": "AI-generated follow-up message",
      //                   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                   "group": "foobar",
      //                   "variableName": "conversation",
      //                   "valueType": "object",
      //                   "type": "conversation",
      //                   "wrap": false,
      //                   "title": "Then",
      //                   "edit": false,
      //                   "actions": {
      //                       "value": [
      //                         { "value": 1 },
      //                       ],
      //                       "type": "action-buttons",
      //                       "options": [
      //                         { "value": 1, "label": "Move chat to the Done folder", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will move the chat to the Done folder in your inbox." },
      //                         { "value": 2, "label": "Unassign chat and mark it as unread", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will unassign the chat and mark it as unread." },
      //                         { "value": 3, "label": "Send chat summary to the email", "type": "email", "context": "moj-email@bla.bla", "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_once_gray.svg", "subtitle": "AI will send the collected info to this email." }
      //                       ],
      //                       "addActionIcon": "SBIconList.PLUS",
      //                       "addActionText": "Add action",
      //                     }
      //                 },
      //                 // "buttons": {
      //                 //   "value": [],
      //                 //   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                 //   "group": "foobar",
      //                 //   "variableName": "...",
      //                 //   "valueType": "string",
      //                 //   "title": "Then",
      //                 //   "value": [
      //                 //     {
      //                 //       "title": "Send a follow up message",
      //                 //       "fixed": true,
      //                 //       "subtitlePrefixIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                 //       "subtitle": "AI will send a follow-up message."
      //                 //     },
      //                 //   ],
      //                 //   "type": "message-buttons",
      //                 //   "returnKey": "responseButtons",
      //                 //   "hideOptional": true
      //                 // },
      //                 // "actionsTest": {
      //                 //   "value": [],
      //                 //   "returnProperties": ["value", "group", "variableName", "valueType"],
      //                 //   "group": "foobar",
      //                 //   "variableName": "...",
      //                 //   "valueType": "string",
      //                 //   "type": "action-buttons",
      //                 //   "options": [
      //                 //     { "value": 1, "label": "Move chat to the Done folder" },
      //                 //     { "value": 2, "label": "Unassign chat and mark it as unread"}
      //                 //   ],
      //                 //   "addActionIcon": "https://sendbee-media-prod.s3.eu-west-1.amazonaws.com/icons/trigger_delay_gray.svg",
      //                 //   "addActionText": "Add action",
      //                 //   "returnKey": "responseActions"
      //                 // }
      //               }
      //             }
      //           }
      //         }
      //       },
      //     ],
      //   },
      // };
      // setTimeout(() => {
      //   this.loadingSchema = false;
      //   console.log('MOCK DATA');
      //   this.assistantsFormSchema = JSON.parse(JSON.stringify(mockData));
      // }, 1000);
    },
    activateAiAssistant(item) {
      // if (item.active) {
      //   this.activateFlowVUEX({ numberId: item.numberId, flowId: item.id, active: true });
      // } else {
      //   this.activateFlowVUEX({ numberId: item.numberId, flowId: item.id, active: false });
      // }
    },
    toTop() {
      this.$refs.stepperScroll.scrollTo(0);
    },
  },
};
</script>

<style lang="scss" scoped>
.flows-wrapper {
  background-color: $white;
  z-index: 998;
}

.stepper-wrapper {
  margin-top: 40px;
  .cover {
    position: absolute;
    height: calc(100% - 48px) !important;
    z-index: 999999;
    background-color: white;
  }
  .stepper {
    max-width: 690px;
    margin: 0 auto;
  }
}

.sticky-top {
  background-color: white;
  top: 20px;

  .mask {
    position: absolute;
    top: -20px;
    left: 0;
    right: 0;
    height: 20px;
    background: white;
    z-index: -1;
  }

  .flow-filters {
    padding-top: unset !important;
  }
}

.no-data {
  border-top: none !important;
  margin-left: 100px;
  margin-right: 100px;
}

::v-deep .block-title .title {
  font-size: 11px;
}

::v-deep .info-card .subtitle {
  max-width: 270px !important;
}
</style>
